import { createContext, useState, useCallback, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, AlertCircle, X, Info } from 'lucide-react';
import { TOAST_TTL_MS } from '@/shared/lib/constants';

type ToastType = 'success' | 'error' | 'info';

interface ToastMessage {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
}

export interface ToastContextType {
  toast: (message: string, type?: ToastType, title?: string) => void;
  success: (message: string, title?: string) => void;
  error: (message: string, title?: string) => void;
}

export const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (message: string, type: ToastType = 'info', title?: string) => {
      const id = Math.random().toString(36).substring(2, 9);
      setToasts((prev) => [...prev, { id, type, title, message }]);

      setTimeout(() => {
        removeToast(id);
      }, TOAST_TTL_MS);
    },
    [removeToast]
  );

  const success = useCallback(
    (message: string, title?: string) => {
      toast(message, 'success', title);
    },
    [toast]
  );
  const error = useCallback(
    (message: string, title?: string) => {
      toast(message, 'error', title);
    },
    [toast]
  );

  return (
    <ToastContext.Provider value={{ toast, success, error }}>
      {children}
      {typeof document !== 'undefined' &&
        createPortal(
          <div className="fixed bottom-6 right-6 z-[200] flex flex-col gap-3 pointer-events-none">
            {toasts.map((t) => (
              <ToastItem
                key={t.id}
                toast={t}
                onRemove={() => {
                  removeToast(t.id);
                }}
              />
            ))}
          </div>,
          document.body
        )}
    </ToastContext.Provider>
  );
};

const ToastItem = ({ toast, onRemove }: { toast: ToastMessage; onRemove: () => void }) => {
  const icons = {
    success: <CheckCircle2 className="h-5 w-5 text-lime-500" />,
    error: <AlertCircle className="h-5 w-5 text-rose-500" />,
    info: <Info className="h-5 w-5 text-blue-500" />,
  };

  const bgColors = {
    success: 'bg-white border-gray-200',
    error: 'bg-white border-rose-200',
    info: 'bg-white border-blue-200',
  };

  return (
    <div
      className={`pointer-events-auto flex w-full max-w-sm items-start gap-4 rounded-2xl border p-4 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.1)] backdrop-blur-md animate-in slide-in-from-right-10 fade-in duration-300 ${bgColors[toast.type]}`}
      role="alert"
    >
      <div className="shrink-0">{icons[toast.type]}</div>
      <div className="flex-1 pt-0.5">
        {toast.title && <p className="nav-label text-gray-900 leading-tight mb-1">{toast.title}</p>}
        <p className="body-md">{toast.message}</p>
      </div>
      <button
        onClick={onRemove}
        className="shrink-0 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-900 transition-colors"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};
